/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useMemo } from "react";
import "./Sidenav.scss";
import { NavLink } from "react-router-dom";
import Box from "@mui/material/Box";
import { Grid2 } from "@mui/material";
import { DndProvider } from "react-dnd";
import FolderTree from "../../features/folder/FolderTree";
import { HTML5Backend } from "react-dnd-html5-backend";
import CloseIcon from "@mui/icons-material/Close";
import { useMenu } from "../../context/MenuContext";
import {
  ModuleNames,
  POSTIF_STATIC_UI_BYPASS,
  POSTIF_STATIC_UI_NAV_LINKS,
} from "../../data/constants";
import { getFilteredMenu } from "../../utils/commonHelper";
import { MenuSection } from "../../types/Menu";

interface SidenavProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
}

const POSTIF_NAV_MODULES = new Set<string>([ModuleNames.PostIFMyForms, ModuleNames.PostIFList]);
const POSTIF_NAV_PATH = "/hiring/postif";

/**
 * Every in-portal PostIF screen (list, new, edit, approval) lives under this href.
 * NavLink's default `end` only marks the exact list URL as active, so the Hiring
 * item went grey on /new and /:id. Prefix-match those routes; leave `end` on
 * every other item so a short path cannot light up an unrelated sibling.
 */
const isPostIfNavItem = (url: string): boolean =>
  url === POSTIF_NAV_PATH || url.startsWith(`${POSTIF_NAV_PATH}/`);

const collapsePostIfNav = (sections: MenuSection[]): MenuSection[] =>
  sections
    .map((section) => {
      const postifItems = section.listItems.filter((item) => POSTIF_NAV_MODULES.has(item.moduleName));
      if (postifItems.length <= 1) {
        return section;
      }

      return {
        ...section,
        listItems: [
          ...section.listItems.filter((item) => !POSTIF_NAV_MODULES.has(item.moduleName)),
          {
            ...postifItems[0],
            title: "Post Interview List",
            url: "/hiring/postif",
          },
        ],
      };
    })
    .filter((section) => section.listItems.length > 0);

const Sidenav: React.FC<SidenavProps> = ({ isOpen, onClose, isMobile }) => {
  const { menu, loading } = useMenu();
  const filteredMenu = getFilteredMenu(menu);

  const displayMenu = useMemo((): MenuSection[] => {
    if (!POSTIF_STATIC_UI_BYPASS) {
      return collapsePostIfNav(filteredMenu);
    }

    const existingModuleNames = new Set(
      filteredMenu.flatMap((section) => section.listItems.map((item) => item.moduleName)),
    );

    const devLinks = POSTIF_STATIC_UI_NAV_LINKS.filter(
      (link) => !existingModuleNames.has(link.moduleName),
    );

    if (devLinks.length === 0) {
      return collapsePostIfNav(filteredMenu);
    }

    return collapsePostIfNav([
      ...filteredMenu,
      {
        id: 9999,
        title: "Hiring",
        icon: "/activities.svg",
        listItems: devLinks.map((link, idx) => ({
          id: idx + 1,
          title: link.title,
          url: link.url,
          icon: "/activities.svg",
          moduleName: link.moduleName,
        })),
      },
    ]);
  }, [filteredMenu]);

  return (
    <>
      {/* Backdrop for mobile */}
      {isMobile && isOpen && (
        <div className="sidenav-backdrop" onClick={onClose}>
          <CloseIcon className="mobile-close-icon" />
        </div>
      )}

      <div
        className={`sidenav ${isMobile ? "mobile-sidenav" : "desktop-sidenav"} ${isOpen ? "open" : ""}`}
      >
        <aside className="aside">
          <div className="navbar-brand-box">
            <img src="/logo-transparent.svg" alt="logo" className="logo" />
          </div>

          <Box component="form">
            <Grid2 container spacing={0}>
              <Grid2 size={12}>
                <div className="menu">
                  {!loading && displayMenu.map((item) => (
                    <div className="item" key={item.id}>
                      <span className="title">{item.title}</span>
                      {item.listItems.map((listItem) => {
                        if (listItem.moduleName === ModuleNames.FileExplorer) {
                          // Render File Explorer section
                          return (
                            <React.Fragment key={listItem.id}>
                              <DndProvider backend={HTML5Backend}>
                                <FolderTree />
                              </DndProvider>
                            </React.Fragment>
                          );
                        }

                        // Render standard menu items
                        return (
                          <NavLink
                            to={listItem.url}
                            end={!isPostIfNavItem(listItem.url)}
                            className={({ isActive }) => `listItem ${isActive ? "active" : ""}`}
                            key={listItem.id}
                            onClick={() => {
                              if (isMobile) onClose();
                            }}
                          >
                            <img src={listItem.icon} alt="" />
                            <span className="listItemTitle">{listItem.title}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </Grid2>
            </Grid2>
          </Box>
        </aside>
      </div>
    </>
  );
};

export default Sidenav;
